import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Card,
  CardBody,
  Spinner,
} from "reactstrap";
import { Btn } from "../../../AbstractElements";
import RouteDropdown from "../../../Component/BasicComponents/Dropdowns/RouteDropdown";
import {
  useGetBrandCampaignStatusData,
  useGetBrandsData,
  useGetClassificationData,
  useGetOrgUnitsDataBrandCampaign,
  useGetOutletTypeListData,
  useGetSGPIListsData,
  useSingleBrandCampaignListData,
} from "../hooks/useBrandCampaign";
import {
  getDesignationPosition,
  getSGPILists,
  handleMedia,
} from "../repositories/BrandCampaignRepository";
import { useGetGradeList } from "../NotificationRepo";
import PaginatedRouteDropdown from "../../../Component/BasicComponents/Dropdowns/PaginatedDropdownWrapper";
import { getBrandData } from "../hooks/ProductRepository";

interface BrandCampaignModalContentProps {
  handleSubmit: (e: React.FormEvent) => void;
  handleClose: () => void;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  formData: any;
  modalStatus: any;
  selectedRow: any;
  BrandCampaignId: any;
  buttonDisable?: boolean;
}

const BrandCampaignModalContent: React.FC<BrandCampaignModalContentProps> = ({
  handleSubmit,
  handleClose,
  setFormData,
  formData,
  modalStatus,
  selectedRow,
  BrandCampaignId,
  buttonDisable,
}) => {
  const hasInitialized = useRef(false);

  const [classificationRows, setClassificationRows] = useState([
    { id: Date.now(), classification: "", min: "", max: "" },
  ]);

  const { data: singleBrandCampaignData } = useSingleBrandCampaignListData({
    id: BrandCampaignId,
    enable: !!BrandCampaignId,
  });
  // orgunit for the entire api
  const orgUnitId = selectedRow?.OrgUnitId || formData?.orgUnit || null;

  const { data: orgUnitData } = useGetOrgUnitsDataBrandCampaign({
    enable: true,
  });
  const { data: outletTypeList } = useGetOutletTypeListData({ enable: true });
  const { data: brandCampaignStatus } = useGetBrandCampaignStatusData({
    enable: true,
  });

  const { data: classificationData } = useGetClassificationData({
    id: orgUnitId,
    enable: !!orgUnitId,
  });

  const {
    data: brandsData,
    isLoading,
    isSuccess,
  } = useGetBrandsData({
    enable: !!orgUnitId,
    params: {
      start: 1,
      length: 10,
      Orgunitid: orgUnitId,
      search: "",
    },
  });

  const { data: sgpiData, isLoading: sgpiLoading } = useGetSGPIListsData({
    enable: !!orgUnitId,
    params: {
      start: 1,
      length: 10,
      search: null,
      sortColumn: null,
      sortOrder: null,
      OrgUnitId: orgUnitId,
      BrandId: null,
      OutlettypeId: null,
    },
  });

  const { data: gradeListData } = useGetGradeList({ enable: true });

  const gradeFormattedData =
    (gradeListData?.rowData?.data || []).map((item: any) => ({
      id: item?.Gradeid,
      label: item?.GradeName,
    })) || [];
  const brandsFormattedData = isLoading
    ? []
    : (brandsData?.data?.data || []).map((item: any) => ({
        id: item?.BrandId,
        label: item?.BrandName,
      }));

  const sgpiDataFormatted = sgpiLoading
    ? []
    : (sgpiData?.data || []).map((item: any, index: any) => ({
        id: item?.SgpiId,
        label: item?.SgpiName,
      }));

  // After designationPosition is created

  const singleBrandCampaignListData = singleBrandCampaignData?.data;

  const BASE_URL = "https://tspc-development.truesales.in/media?id=";

  const orgUnitFormattedData =
    Object.entries(orgUnitData?.data || {})?.map((item: any, index) => ({
      id: item[0],
      label: item[1],
    })) || [];

  const brandCampaignStatusFormattedData =
    Object.entries(brandCampaignStatus?.data?.Types || {})?.map(
      (item: any, index) => ({
        id: item[0],
        label: item[1],
      })
    ) || [];

  const isSuspendedFormattedData =
    Object.entries(brandCampaignStatus?.data?.Enabled || {})?.map(
      (item: any, index) => ({
        id: item[0],
        label: item[1],
      })
    ) || [];

  const outletTypeListFormattedData =
    outletTypeList?.data?.map((item: any, index: any) => ({
      id: item?.OutlettypeId,
      label: item?.OutlettypeName,
    })) || [];

  const classificationFormattedData = [
    { id: "all", label: "All" },
    ...(classificationData?.data?.map((item: any, index: any) => ({
      id: item?.Id,
      label: item?.Classification,
    })) || []),
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Convert specific fields to numbers
    const numericFields = ["adminApproval", "attachment", "leavePoints"];
    const updatedValue = numericFields.includes(name)
      ? value.replace(/[^0-9]/g, "") // Allow only numbers
      : value; // Keep string values as they are

    setFormData((prevState: any) => ({
      ...prevState,
      [name]: numericFields.includes(name)
        ? updatedValue === ""
          ? ""
          : Number(updatedValue)
        : updatedValue,
    }));
  };

  const handleDropdownChange = (name: string, value: any) => {
    setFormData((prev: any) => {
      // if orgUnit changes, reset focusBrands, sgpiBrands, positions
      if (name === "orgUnit") {
        return {
          ...prev,
          orgUnit: value,
          focusBrands: [],
          sgpiBrands: [],
          position: [],
        };
      }

      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const mediaId = await handleMedia(file);
        setFormData((prev: any) => ({ ...prev, media: mediaId }));
      } catch (error) {
        console.error("Media upload failed:", error);
      }
    }
  };

  const handleAddRow = () => {
    setClassificationRows((prev) => [
      ...prev,
      {
        id: Date.now(),
        classification: "",
        min: "",
        max: "",
      },
    ]);
  };

  const handleRemoveRow = (id: any) => {
    setClassificationRows((prev) => prev.filter((row) => row?.id !== id));
  };

  const handleRowChange = (id: any, field: any, value: any) => {
    // Update the row data
    const updatedRows = classificationRows.map((row) =>
      row?.id === id ? { ...row, [field]: value } : row
    );
    setClassificationRows(updatedRows);

    // Extract the values into the desired format
    const classifications = updatedRows.map((row) => row.classification);
    const minValues = updatedRows.map((row) => row?.min);
    const maxValues = updatedRows.map((row) => row?.max);

    // Update the formData with the extracted values
    setFormData((prevFormData: any) => ({
      ...prevFormData,
      classifications,
      minValues,
      maxValues,
    }));
  };

  const onUpdateData = () => {
    if (
      modalStatus === "Update" &&
      sgpiDataFormatted?.length > 0 &&
      sgpiDataFormatted?.length > 0 &&
      gradeFormattedData &&
      singleBrandCampaignListData &&
      orgUnitFormattedData
    ) {
      const matchedOrgUnit = orgUnitFormattedData.find(
        (item) => item.label === selectedRow?.UnitName
      )?.id;

      // Map BrandCampaignClassification data
      const mappedClassificationRows =
        singleBrandCampaignListData?.BrandCampaignClassification?.map(
          (item: any) => ({
            id: item.BrandCampiagnClassificationId,
            classification: item.ClassificationId,
            min: item.Minimum,
            max: item.Maximum,
          })
        ) || [];

      // Extract classification, min, max arrays
      const classifications = mappedClassificationRows.map(
        (item: any) => item.classification
      );
      const minValues = mappedClassificationRows.map((item: any) => item.min);
      const maxValues = mappedClassificationRows.map((item: any) => item.max);

      // Set initial classificationRows
      setClassificationRows(
        mappedClassificationRows.length > 0
          ? mappedClassificationRows
          : [{ id: Date.now(), classification: "", min: "", max: "" }]
      );

      setFormData({
        campaignName: singleBrandCampaignListData?.CampiagnName,
        campaignCode: singleBrandCampaignListData?.BrandCampiagnCode,
        startDate: singleBrandCampaignListData?.StartDate,
        endDate: singleBrandCampaignListData?.EndDate,
        lockingDate: singleBrandCampaignListData?.LockingDate,
        campaignType: singleBrandCampaignListData?.CampiagnType,
        outletTypeId: singleBrandCampaignListData?.OutlettypeId,
        focusBrands:
          singleBrandCampaignListData?.FocusBrandsArray?.map((item: any) => ({
            id: item.BrandId,
            label: item.BrandName,
          })) || [],
        sgpiBrands:
          singleBrandCampaignListData?.SgpiBrandsArray?.map((item: any) => ({
            id: item.BrandId,
            label: item.BrandName,
          })) || [],
        isSuspended:
          isSuspendedFormattedData.find(
            (item: any) =>
              item?.id.toString() ===
              singleBrandCampaignListData?.IsSuspended?.toString()
          )?.id || "",
        orgUnit: matchedOrgUnit || "",
        classifications: classifications || [],
        minValues: minValues || [],
        maxValues: maxValues || [],
        position:
          singleBrandCampaignListData?.Positions?.map((pos: any) => ({
            id: pos?.PositionId,
            label: pos?.PositionName,
          })) || [],
        description: singleBrandCampaignListData?.Description,
        materialUrl: singleBrandCampaignListData?.Material,
        media: singleBrandCampaignListData?.Media,
        Grades:
          singleBrandCampaignListData?.Grades?.map((item: any) =>
            item.Gradeid.toString()
          ) || [],
      });
    }
  };

  useEffect(() => {
    if (
      !hasInitialized.current &&
      modalStatus === "Update" &&
      sgpiDataFormatted?.length > 0 &&
      brandsFormattedData?.length > 0 &&
      gradeFormattedData &&
      gradeFormattedData?.length > 0 &&
      singleBrandCampaignListData &&
      orgUnitFormattedData &&
      isSuspendedFormattedData
    ) {
      onUpdateData();
      hasInitialized.current = true;
    }
  }, [
    sgpiDataFormatted,
    brandsFormattedData,
    gradeFormattedData,
    singleBrandCampaignListData,
    orgUnitFormattedData,
    isSuspendedFormattedData,
    modalStatus,
  ]);

  return sgpiDataFormatted &&
    sgpiDataFormatted?.length === 0 &&
    brandsFormattedData &&
    brandsFormattedData?.length === 0 &&
    gradeFormattedData &&
    gradeFormattedData?.length === 0 ? (
    <div
      className="d-flex justify-content-center align-items-center"
      style={{ height: "60vh" }}
    >
      <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
    </div>
  ) : (
    <Form onSubmit={handleSubmit}>
      <Row>
        {/* Left Section */}
        <Col md={6}>
          <Card className="shadow-sm border-0">
            <CardBody>
              <h5>Campaign Basic Details</h5>
              <FormGroup>
                <Label>
                  Campaign Name <span style={{ color: "red" }}>*</span>
                </Label>
                <Input
                  value={formData?.campaignName}
                  onChange={handleInputChange}
                  name="campaignName"
                  type="text"
                  placeholder="Enter campaign name"
                  required
                />
              </FormGroup>
              {modalStatus === "Update" && (
                <FormGroup>
                  <Label>
                    Campaign Code <span style={{ color: "red" }}>*</span>
                  </Label>
                  <Input
                    type="text"
                    placeholder="Auto-generated"
                    disabled
                    value={formData?.campaignCode}
                  />
                </FormGroup>
              )}
              <FormGroup>
                <Label>
                  Description <span style={{ color: "red" }}>*</span>
                </Label>
                <Input
                  type="textarea"
                  placeholder="Enter campaign description"
                  name="description"
                  value={formData?.description}
                  onChange={handleInputChange}
                />
              </FormGroup>
              <FormGroup>
                <RouteDropdown
                  options={brandCampaignStatusFormattedData || []}
                  onChange={(value) =>
                    handleDropdownChange("campaignType", value)
                  }
                  asterisk={true}
                  value={formData?.campaignType}
                  DropdownHeading="Campaign Type"
                />
              </FormGroup>
              <FormGroup>
                <RouteDropdown
                  options={outletTypeListFormattedData || []}
                  onChange={(value) =>
                    handleDropdownChange("outletTypeId", value)
                  }
                  asterisk={true}
                  value={formData?.outletTypeId}
                  DropdownHeading="Customer Type"
                />
              </FormGroup>
            </CardBody>
          </Card>
          <Card className="shadow-sm border-0 mt-3">
            <CardBody>
              <h5>Campaign Configuration</h5>
              <FormGroup>
                <Label>Material URL</Label>
                <Input
                  type="text"
                  placeholder="Enter material URL"
                  value={formData?.materialUrl}
                  onChange={handleInputChange}
                  name="materialUrl"
                />
              </FormGroup>
              <FormGroup>
                <PaginatedRouteDropdown
                  key={formData?.orgUnit}
                  isDisabled={!orgUnitId}
                  DropdownHeading="Focus Brand"
                  isMulti={true}
                  dependencies={[formData?.orgUnit]}
                  fetchOptions={async (page: any, search: any) => {
                    const res = await getBrandData({
                      page,
                      limit: 10,
                      search: search || "",
                      Orgunitid: formData?.orgUnit,
                    });
                    return Object.values(res?.data?.data || {}).map(
                      (key: any) => ({
                        id: key.BrandId,
                        label: key.BrandName,
                      })
                    );
                  }}
                  onChange={(val: any) => {
                    handleDropdownChange("focusBrands", val);
                  }}
                  initialSelectedOptions={formData?.focusBrands}
                />
              </FormGroup>
              <FormGroup>
                <PaginatedRouteDropdown
                  key={formData?.orgUnit}
                  isDisabled={!orgUnitId}
                  DropdownHeading="Sgpi Brand"
                  isMulti={true}
                  dependencies={[formData?.orgUnit]}
                  fetchOptions={async (page: any, search: any) => {
                    const res = await getSGPILists({
                      page,
                      limit: 10,
                      search: search || "",
                      OrgUnitId: formData?.orgUnit,
                    });
                    return (res?.data || [])?.map((key: any) => ({
                      id: key?.SgpiId,
                      label: key?.SgpiName,
                    }));
                  }}
                  onChange={(val: any) => {
                    console.log("abal", val);
                    handleDropdownChange("sgpiBrands", val);
                  }}
                  initialSelectedOptions={formData?.sgpiBrands}
                />
              </FormGroup>
              <FormGroup>
                <RouteDropdown
                  options={isSuspendedFormattedData || []}
                  onChange={(value) =>
                    handleDropdownChange("isSuspended", value)
                  }
                  value={formData?.isSuspended}
                  DropdownHeading="Is Suspended?"
                />
              </FormGroup>
              <FormGroup>
                <Label>Media</Label>
                <Input
                  type="file"
                  onChange={handleFileChange}
                  accept="image/*"
                />
              </FormGroup>

              {formData.media && (
                <div className="row mb-3">
                  <div className="col-md-6" style={{ marginBottom: "10px" }}>
                    <label style={{ fontWeight: "500", display: "block" }}>
                      Uploaded Media
                    </label>

                    <img
                      src={
                        formData?.media
                          ? `${BASE_URL}${formData?.media}`
                          : "https://via.placeholder.com/100?text=No+Image"
                      }
                      alt="Uploaded Media"
                      style={{
                        width: "100px",
                        height: "100px",
                        borderRadius: "8px",
                        marginTop: "8px",
                      }}
                    />
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>

        {/* Right Section */}
        <Col md={6}>
          <Card className="shadow-sm border-0">
            <CardBody>
              <h5>Campaign Dates</h5>
              <FormGroup>
                <Label>
                  Start Date <span style={{ color: "red" }}>*</span>
                </Label>
                <Input
                  type="date"
                  required
                  value={formData?.startDate}
                  onChange={handleInputChange}
                  name="startDate"
                />
              </FormGroup>
              <FormGroup>
                <Label>
                  End Date <span style={{ color: "red" }}>*</span>
                </Label>
                <Input
                  type="date"
                  required
                  value={formData?.endDate}
                  onChange={handleInputChange}
                  name="endDate"
                />
              </FormGroup>
              <FormGroup>
                <Label>
                  Locking Date <span style={{ color: "red" }}>*</span>
                </Label>
                <Input
                  type="date"
                  required
                  value={formData?.lockingDate}
                  onChange={handleInputChange}
                  name="lockingDate"
                />
              </FormGroup>
            </CardBody>
          </Card>
          <Card className="shadow-sm border-0 mt-3">
            <CardBody>
              <h5>Campaign Employees</h5>
              <FormGroup>
                <RouteDropdown
                  options={orgUnitFormattedData || []}
                  onChange={(value) => handleDropdownChange("orgUnit", value)}
                  value={formData?.orgUnit}
                  DropdownHeading="Org Unit"
                  asterisk={true}
                />
              </FormGroup>
              <FormGroup>
                <RouteDropdown
                  options={gradeFormattedData || []}
                  onChange={(value) => handleDropdownChange("Grades", value)}
                  value={formData?.Grades}
                  DropdownHeading="Grade"
                  isMulti={true}
                  asterisk={true}
                />
              </FormGroup>
              <FormGroup>
                <PaginatedRouteDropdown
                  DropdownHeading="Position"
                  isMulti={true}
                  asterisk={true}
                  dependencies={[formData?.Grades, orgUnitId]}
                  fetchOptions={async (page: any, search: any) => {
                    if (!formData?.Grades?.length || !orgUnitId) return [];

                    const params = {
                      start: page,
                      length: 10,
                      search: search || "",
                      orgUnitId: Number(orgUnitId),
                      GradesId: formData?.Grades || [],
                    };

                    const res = await getDesignationPosition(params);

                    return (
                      res?.data?.data?.map((item: any) => ({
                        id: item?.PositionId,
                        label: item?.PositionName,
                      })) || []
                    );
                  }}
                  onChange={(val: any) => handleDropdownChange("position", val)}
                  initialSelectedOptions={formData?.position}
                />
              </FormGroup>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Campaign Classification */}

      <Row>
        <Col md={12}>
          <Card className="shadow-sm border-0 mt-3">
            <CardBody>
              <h5>Campaign Classification (Speciality)</h5>

              {classificationRows.map((row, index) => (
                <Row className="align-items-end" key={row.id}>
                  <Col md={4}>
                    <FormGroup>
                      <RouteDropdown
                        options={classificationFormattedData || []}
                        onChange={(value) =>
                          handleRowChange(row?.id, "classification", value)
                        }
                        isDisabled={!orgUnitId}
                        value={row?.classification}
                        DropdownHeading="Classification"
                        asterisk={true}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label>
                        Minimum <span style={{ color: "red" }}>*</span>
                      </Label>
                      <Input
                        type="number"
                        value={row?.min}
                        onChange={(e) =>
                          handleRowChange(row?.id, "min", e.target.value)
                        }
                        placeholder="Enter minimum value"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label>
                        Maximum <span style={{ color: "red" }}>*</span>
                      </Label>
                      <Input
                        type="number"
                        value={row?.max}
                        onChange={(e) =>
                          handleRowChange(row?.id, "max", e.target.value)
                        }
                        placeholder="Enter maximum value"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2} className="d-flex justify-content-center">
                    {index === 0 ? (
                      <Button color="primary" onClick={handleAddRow}>
                        +
                      </Button>
                    ) : (
                      <Button
                        color="danger"
                        onClick={() => handleRemoveRow(row?.id)}
                      >
                        −
                      </Button>
                    )}
                  </Col>
                </Row>
              ))}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Buttons */}
      <div className="d-flex justify-content-start mt-3 gap-2">
        <Btn
          style={{ backgroundColor: "#43B9B2", color: "white" }}
          type="submit"
          onClick={handleSubmit}
          disabled={buttonDisable}
        >
          Submit
        </Btn>
        <Btn
          style={{
            backgroundColor: "#F9FAFC",
            color: "#000",
            border: "1px solid #E0E0E0",
          }}
          type="button"
          onClick={handleClose}
        >
          Cancel
        </Btn>
      </div>
    </Form>
  );
};

export default BrandCampaignModalContent;
